/**
 * Skaliert ein Bild auf maximal maxWidth x maxHeight und gibt es als Base64 Data-URL zurück.
 * Das Seitenverhältnis bleibt erhalten.
 * Ausgabeformat: JPEG mit Qualität 0.85 (guter Kompromiss aus Qualität und Größe).
 */
export function resizeImageToBase64(
  file: File,
  maxWidth: number,
  maxHeight: number,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = () => {
      img.onload = () => {
        let { width, height } = img;

        // Skalierung berechnen (Seitenverhaeltnis beibehalten)
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        // Canvas erstellen und Bild zeichnen
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas-Kontext konnte nicht erstellt werden'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Als JPEG Base64 exportieren (hohe Qualität für PDF-Export)
        const base64 = canvas.toDataURL('image/jpeg', 0.92);
        resolve(base64);
      };

      img.onerror = () => reject(new Error('Bild konnte nicht geladen werden'));
      img.src = reader.result as string;
    };

    reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
    reader.readAsDataURL(file);
  });
}

/**
 * Erzeugt ein zugeschnittenes Bild aus den Crop-Koordinaten von react-easy-crop.
 * Gibt ein quadratisches JPEG in outputSize x outputSize als Base64 Data-URL zurück.
 */
export function getCroppedImage(
  imageSrc: string,
  cropArea: { x: number; y: number; width: number; height: number },
  outputSize: number = 1200,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = outputSize;
      canvas.height = outputSize;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas-Kontext konnte nicht erstellt werden'));
        return;
      }

      // Weißer Hintergrund (für Transparenz-Fallback)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, outputSize, outputSize);

      // Zugeschnittenen Bereich auf Output-Größe zeichnen
      ctx.drawImage(
        img,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
        0,
        0,
        outputSize,
        outputSize,
      );

      const base64 = canvas.toDataURL('image/jpeg', 0.95);
      resolve(base64);
    };
    img.onerror = () => reject(new Error('Bild konnte nicht geladen werden'));
    img.crossOrigin = 'anonymous';
    img.src = imageSrc;
  });
}

/**
 * Liest eine Datei als Data-URL (Base64).
 */
export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
    reader.readAsDataURL(file);
  });
}
